import React, { useMemo, useRef, useState } from "react";
import config from "../api/config";
import Button from "../components/element/Button";
import EntityApi from "../api/EntityApi";

type UploadStatus = "queued" | "uploading" | "success" | "error" | "canceled";

type UploadItem = {
    id: string;
    file: File;
    status: UploadStatus;
    progress: number; // 0..100 (если API не поддерживает прогресс — будет 0/100)
    url?: string;
    name?: string;
    error?: string;
    abortController?: AbortController;
};
type UploadedFile = { url: string; name: string };

type Props = {
    chapterId: string;
    productId: string;

    /**
     * Вызовется каждый раз, когда меняется массив успешно загруженных URL.
     * Удобно для последующей отправки на другой эндпоинт.
     */
    onUploadedUrlsChange?: (urls: UploadedFile[]) => void;

    /**
     * Вызовется один раз, когда текущая партия загрузок закончится
     */
    onBatchFinished?: (result: { urls: string[]; failed: UploadItem[] }) => void;

    /**
     * Можно ограничить типы файлов, например: "image/*,.pdf"
     */
    accept?: string;

    /**
     * Если захочешь позже включить параллельность, но сейчас по умолчанию 1 (по очереди)
     */
    concurrency?: number;

    disabled?: boolean;

    onReload?: () => void;
};

/**
 * Пример “API загрузки одного файла”.
 * Подставь свой реальный вызов: axios/fetch, endpoint, поля формы и т.д.
 *
 * ВАЖНО: Чтобы иметь прогресс — обычно удобнее axios (onUploadProgress) или XHR.
 * Ниже — fetch без прогресса (progress будет 0 -> 100).
 */
async function uploadSingleFile(params: {
    chapterId: string;
    productId: string;
    file: File;
    signal?: AbortSignal;
}): Promise<{ url: string; name: string }> {
    const { chapterId, productId, file, signal } = params;

    const form = new FormData();
    form.append("file", file);
    //form.append("chapterId", chapterId);
    //form.append("productId", productId);

    const res = await fetch(config.host + '/api/private/file', {
        method: "POST",
        body: form,
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token')
        },
        signal,
    });

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Upload failed: ${res.status}`);
    }

    // ожидаем { url: "https://..." }
    const json = await res.json();
    const url = json?.content?.item?.url;

    if (!url) {
        throw new Error("URL not found in API response");
    }

    return { url: url, name: file.name };
}

function makeId() {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function UploadMaterialFilesModal({
                                      chapterId,
                                      productId,
                                      onUploadedUrlsChange,
                                      onBatchFinished,
                                      accept,
                                      concurrency = 1,
                                      disabled,
                                      onReload
                                  }: Props) {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [items, setItems] = useState<UploadItem[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const [isLoadingCreate, setLoadingCreate] = useState(false);

    const uploadedUrls = useMemo(
        () =>
            items.filter((i) => i.status === "success" && i.url).map((i) => ({
            url: i.url!,
            name: i.name!,
        })) ,
    [items]
);

    // сообщаем наружу массив url
    React.useEffect(() => {
        onUploadedUrlsChange?.(uploadedUrls);
    }, [uploadedUrls, onUploadedUrlsChange]);

    async function onMaterialsCreate() {
        try {
            setLoadingCreate(true);

            await Promise.all(
                uploadedUrls.map((fileItem) =>
                    EntityApi.put("private/material", {
                        title: fileItem.name.replace(/\.[^/.]+$/, ""),
                        chapter: chapterId,
                        product: productId,
                        type: "file",
                        content: fileItem.url,
                        is_modal: true,
                        custom_type: "default",
                    })
                )
            );

            // ✅ все файлы отправлены — очищаем массив
            setItems([]);

        } catch (error) {
            alert("Ошибка добавления одного или нескольких материалов");
        } finally {
            setLoadingCreate(false);
        }
    }

    function addFiles(files: FileList | null) {
        if (!files || files.length === 0) return;

        const next: UploadItem[] = Array.from(files).map((file) => ({
            id: makeId(),
            file,
            status: "queued",
            progress: 0,
        }));

        setItems((prev) => {
            // опционально: дедуп по name+size+lastModified
            const existing = new Set(
                prev.map((p) => `${p.file.name}_${p.file.size}_${p.file.lastModified}`)
            );
            const filtered = next.filter(
                (n) => !existing.has(`${n.file.name}_${n.file.size}_${n.file.lastModified}`)
            );
            return [...prev, ...filtered];
        });

        // сброс input, чтобы можно было выбрать те же файлы повторно
        if (inputRef.current) inputRef.current.value = "";
    }

    function removeItem(id: string) {
        setItems((prev) => prev.filter((x) => x.id !== id));
    }

    function clearAll() {
        // отменяем активные
        setItems((prev) => {
            prev.forEach((i) => i.abortController?.abort());
            return [];
        });
    }

    function cancelItem(id: string) {
        setItems((prev) =>
            prev.map((x) => {
                if (x.id !== id) return x;
                x.abortController?.abort();
                return { ...x, status: "canceled", error: "Canceled by user" };
            })
        );
    }

    async function startUpload() {
        if (isRunning) return;

        setIsRunning(true);

        try {
            // очередь — только queued
            // параллельность можно поднять, но сейчас по умолчанию 1
            const queueIds = items.filter((i) => i.status === "queued").map((i) => i.id);
            if (queueIds.length === 0) return;

            let cursor = 0;
            const workers = new Array(Math.max(1, concurrency)).fill(0).map(async () => {
                while (true) {
                    const id = queueIds[cursor++];
                    if (!id) return;

                    // помечаем uploading + создаём AbortController
                    const controller = new AbortController();

                    setItems((prev) =>
                        prev.map((x) =>
                            x.id === id
                                ? {
                                    ...x,
                                    status: "uploading",
                                    progress: 0,
                                    error: undefined,
                                    abortController: controller,
                                }
                                : x
                        )
                    );

                    // берём актуальный item/file
                    const current = (() => {
                        // читаем file из замыкания через state небезопасно,
                        // но здесь id уникальный и file не меняется — норм.
                        // Если хочешь строго — вынеси items в ref.
                        return items.find((x) => x.id === id);
                    })();

                    const file = current?.file;
                    if (!file) {
                        setItems((prev) =>
                            prev.map((x) =>
                                x.id === id ? { ...x, status: "error", error: "File not found" } : x
                            )
                        );
                        continue;
                    }

                    try {
                        // если fetch — прогресса нет, ставим 0 -> 100
                        const { url, name } = await uploadSingleFile({
                            chapterId,
                            productId,
                            file,
                            signal: controller.signal,
                        });

                        setItems((prev) =>
                            prev.map((x) =>
                                x.id === id
                                    ? {
                                        ...x,
                                        status: "success",
                                        progress: 100,
                                        url,
                                        name,
                                        abortController: undefined,
                                    }
                                    : x
                            )
                        );
                    } catch (e: any) {
                        const isAbort =
                            e?.name === "AbortError" || String(e?.message || "").toLowerCase().includes("abort");

                        setItems((prev) =>
                            prev.map((x) =>
                                x.id === id
                                    ? {
                                        ...x,
                                        status: isAbort ? "canceled" : "error",
                                        error: isAbort ? "Canceled" : (e?.message ?? "Upload error"),
                                        progress: isAbort ? 0 : x.progress,
                                        abortController: undefined,
                                    }
                                    : x
                            )
                        );
                    }
                }
            });

            await Promise.all(workers);

            // итог партии
            const finalUrls = uploadedUrls; // из memo (может быть чуть устаревшим в моменте)
            // для точности считаем прямо из state после завершения:
            setItems((prev) => {
                const urls = prev.filter((i) => i.status === "success" && i.url).map((i) => i.url!);
                const failed = prev.filter((i) => i.status === "error");
                onBatchFinished?.({ urls, failed });
                return prev;
            });
        } finally {
            setIsRunning(false);
        }
    }

    function retryFailed() {
        setItems((prev) =>
            prev.map((x) =>
                x.status === "error" || x.status === "canceled"
                    ? { ...x, status: "queued", progress: 0, error: undefined }
                    : x
            )
        );
    }

    return (
        <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={accept}
                    disabled={disabled || isRunning}
                    onChange={(e) => addFiles(e.target.files)}
                />

                {/*<button type="button" disabled={disabled || isRunning} onClick={startUpload}>
                    Загрузить
                </button>

                <button
                    type="button"
                    disabled={disabled || isRunning || items.length === 0}
                    onClick={retryFailed}
                >
                    Повторить ошибки
                </button>*/}

                <div className={'flex gap-4'}>
                    <Button type={'default'} text={"Загрузить"}  disabled={disabled || isRunning} onClick={startUpload}></Button>
                    <Button type={'errorOutline'} text={"Очистить"}  disabled={disabled || items.length === 0} onClick={clearAll}></Button>
                </div>


                {/*<div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.8 }}>
                    chapterId: <b>{chapterId}</b> • productId: <b>{productId}</b>
                </div>*/}
            </div>

            {items.length > 0 && (
                <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
                    <div style={{ display: "grid", gap: 10 }}>
                        {items.map((i) => (
                            <div key={i.id} style={{ display: "grid", gap: 6 }}>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 14 }}>
                                            <b>{i.file.name}</b>{" "}
                                            <span style={{ fontSize: 12, opacity: 0.7 }}>
                        ({Math.round(i.file.size / 1024)} KB)
                      </span>
                                        </div>
                                        <div style={{ fontSize: 12, opacity: 0.75 }}>
                                            Статус: <b>{i.status}</b>
                                            {i.error ? ` — ${i.error}` : ""}
                                        </div>
                                        {i.url && (
                                            <div style={{ fontSize: 12 }}>
                                                URL: <a href={i.url} target="_blank" rel="noreferrer">{i.url}</a>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", gap: 6 }}>
                                        {i.status === "uploading" && (
                                            <button type="button" onClick={() => cancelItem(i.id)}>
                                                Отменить
                                            </button>
                                        )}
                                        {(i.status === "queued" || i.status === "error" || i.status === "canceled") && (
                                            <button type="button" onClick={() => removeItem(i.id)}>
                                                Удалить
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div style={{ height: 8, background: "#eee", borderRadius: 999 }}>
                                    <div
                                        style={{
                                            height: 8,
                                            width: `${i.progress}%`,
                                            background: "#999",
                                            borderRadius: 999,
                                            transition: "width 200ms linear",
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ fontSize: 12, opacity: 0.8 }}>
                Успешно загружено URL: <b>{uploadedUrls.length}</b>
            </div>
            {uploadedUrls.length > 0 && !isLoadingCreate && (
                <div className={'w-1/2'}>
                    <Button type={'default'} text={"Создать материалы"} onClick={onMaterialsCreate}></Button>
                </div>
            )}
            {
                isLoadingCreate && (
                    <div>Создаем материалы ... подождите</div>
                )
            }
        </div>
    );
}
